import { useState, useEffect, useContext, useMemo } from 'react';
import { Trip } from '../types';
import { FirebaseAuthContext } from '../context/FirebaseAuthContext';
import {
  getUserTrips,
  getSharedTrips,
  createTrip as firebaseCreateTrip,
  updateTrip as firebaseUpdateTrip,
  deleteTrip as firebaseDeleteTrip,
  shareTrip,
  subscribeToUserTrips,
  subscribeToSharedTrips
} from '../services/firebase';

export function useFirebaseTrips() {
  const authContext = useContext(FirebaseAuthContext);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [sharedTrips, setSharedTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authContext?.user) {
      console.log('No user, clearing trips');
      setTrips([]);
      setSharedTrips([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Set up real-time subscriptions
    const unsubscribeUserTrips = subscribeToUserTrips(authContext.user.id, (userTrips) => {
      console.log('Real-time update - user trips:', userTrips);
      setTrips(userTrips);
      setIsLoading(false);
    });

    let unsubscribeSharedTrips: (() => void) | undefined;
    unsubscribeSharedTrips = subscribeToSharedTrips(authContext.user.id, (shared) => {
      console.log('Real-time update - shared trips:', shared);
      setSharedTrips(shared);
    });

    // Cleanup subscriptions on unmount or user change
    return () => {
      unsubscribeUserTrips();
      if (unsubscribeSharedTrips) {
        unsubscribeSharedTrips();
      }
    };
  }, [authContext?.user?.id]);

  // Keep loadTrips for manual refresh
  const loadTrips = async () => {
    if (!authContext?.user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const [userTrips, shared] = await Promise.all([
        getUserTrips(),
        getSharedTrips()
      ]);
      setTrips(userTrips);
      setSharedTrips(shared);
    } catch (err: any) {
      console.error('Error loading trips:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const createTrip = async (tripData: Omit<Trip, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setError(null);
      console.log('Creating trip with data:', tripData);
      const tripId = await firebaseCreateTrip(tripData);
      console.log('Trip created with ID:', tripId);
      // No need to reload - real-time subscription will update
      return tripId;
    } catch (err: any) {
      console.error('Error creating trip:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateTrip = async (tripId: string, updates: Partial<Trip>) => {
    try {
      setError(null);
      await firebaseUpdateTrip(tripId, updates);
      // No need to reload - real-time subscription will update
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteTrip = async (tripId: string) => {
    try {
      setError(null);
      await firebaseDeleteTrip(tripId);
      // No need to reload - real-time subscription will update
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const shareTripWithUser = async (tripId: string, email: string) => {
    try {
      setError(null);
      await shareTrip(tripId, email);
      // No need to reload - real-time subscription will update
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const allTrips = useMemo(() => {
    // Create a Set of trip IDs from the main trips array to easily check for duplicates
    const tripIds = new Set(trips.map(t => t.id));
    
    // Filter sharedTrips to only include trips not already in the main trips array
    const uniqueSharedTrips = sharedTrips.filter(st => !tripIds.has(st.id));
    
    return [...trips, ...uniqueSharedTrips];
  }, [trips, sharedTrips]);

  return {
    trips,
    sharedTrips,
    allTrips,
    isLoading,
    error,
    createTrip,
    updateTrip,
    deleteTrip,
    shareTrip: shareTripWithUser,
    refreshTrips: loadTrips
  };
}